import React from 'react'
import {useTranslation} from 'react-i18next'
import {ActivationDetail, Touchpoint, TouchpointStatus} from '../../domain/entities/Activation.ts'
import {useActivationTouchpointsByCode} from '../../hooks/useActivations.ts'
import {ActivationRouteBadge, ActivationStatePill, ActivationStepper} from './ActivationParts.tsx'
import {touchpointLabelKey, useActivationFormatters} from './activationView.ts'

const STATUS_CLASSES: Partial<Record<TouchpointStatus, string>> = {
    DELIVERED: 'bg-[#E3F3EA] text-[#12734A]',
    OPENED: 'bg-[#E3F3EA] text-[#12734A]',
    CLICKED: 'bg-[#E3F3EA] text-[#12734A]',
    SENT: 'bg-[#E8EEFC] text-[#2C4BAA]',
    SENDING: 'bg-[#E8EEFC] text-[#2C4BAA]',
    BOUNCED: 'bg-[#FDECEC] text-[#B3261E]',
    FAILED: 'bg-[#FDECEC] text-[#B3261E]',
    UNKNOWN: 'bg-orange-50 text-orange-800',
    SIMULATED: 'bg-orange-50 text-orange-800',
}

const byWhen = (a: Touchpoint, b: Touchpoint) => (a.sentAt ?? a.scheduledAt ?? 0) - (b.sentAt ?? b.scheduledAt ?? 0)

// One activation with its touchpoints: key, channel, scheduled/sent time,
// Brevo status and skip reason. Shared by the admin code history and the
// deep-linked detail on "No activados" (which passes its own header).
export const ActivationTouchpointsCard: React.FC<{activation: ActivationDetail; header?: React.ReactNode}> = ({activation, header}) => {
    const {t} = useTranslation('dashboard')
    const {formatDateTime} = useActivationFormatters()
    // Skip reasons are lowercase engine values; unknown ones show raw.
    const skipReasonLabel = (reason: string) =>
        t(`activation.skipReason.${reason}`, {defaultValue: t('activation.skipReason.other', {reason})})

    return (
        <div className="flex flex-col gap-2.5 rounded-xl border border-neutralBlack-200 p-3.5">
            {header ?? (
                <span className="flex items-center gap-2 flex-wrap">
                    <ActivationRouteBadge route={activation.route}/>
                    <ActivationStatePill state={activation.state}/>
                    <ActivationStepper activation={activation}/>
                </span>
            )}
            {(activation.touchpoints ?? []).length === 0 ? (
                <p className="text-[12.5px] text-neutralBlack-600">{t('activation.touchpoints.empty')}</p>
            ) : (
                <ul className="flex flex-col divide-y divide-neutralBlack-100">
                    {[...activation.touchpoints].sort(byWhen).map((tp) => (
                        <li key={tp.id ?? tp.key} className="py-2 flex flex-col gap-0.5">
                            <span className="flex items-center gap-2 flex-wrap">
                                <span className="text-[13px] font-medium text-neutralBlack-900">
                                    {t(touchpointLabelKey(tp.key), {defaultValue: tp.key})}
                                </span>
                                <span className="text-[11px] font-mono text-neutralBlack-600">{tp.key}</span>
                                <span className={`inline-flex items-center h-5 px-2 rounded-full text-[11px] font-medium ${STATUS_CLASSES[tp.status] ?? 'bg-neutralBlack-100 text-neutralBlack-700'}`}>
                                    {t(`activation.touchStatus.${tp.status}`, {defaultValue: tp.status})}
                                </span>
                            </span>
                            <span className="text-[12px] text-neutralBlack-600">
                                {t(`activation.channel.${tp.channel}`, {defaultValue: tp.channel})}
                                {' · '}{t(`activation.touchpoints.recipient.${tp.recipient}`, {defaultValue: tp.recipient})}
                                {' · '}{t('activation.touchpoints.scheduledAt', {date: formatDateTime(tp.scheduledAt)})}
                                {tp.sentAt != null && <>{' · '}{t('activation.touchpoints.sentAt', {date: formatDateTime(tp.sentAt)})}</>}
                            </span>
                            {tp.skipReason && (
                                <span className="text-[12px] text-orange-700">
                                    {t('activation.touchpoints.skipReason', {reason: skipReasonLabel(tp.skipReason)})}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

// Admin code history, "Contactos automáticos": the touchpoints of every
// activation behind this code (a generic C1 code can have several).
export const ActivationTouchpointsSection: React.FC<{codeId: string; agencyId?: string}> = ({codeId, agencyId}) => {
    const {t} = useTranslation('dashboard')
    const {activationDetails, isLoadingActivationDetails, activationDetailsError} = useActivationTouchpointsByCode(codeId, agencyId)

    return (
        <section aria-labelledby={`touchpoints-${codeId}`} className="flex flex-col gap-3">
            <h3 id={`touchpoints-${codeId}`} className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-600">
                {t('activation.touchpoints.title')}
            </h3>

            {isLoadingActivationDetails ? (
                <div className="h-14 rounded-xl bg-neutralBlack-50 animate-pulse"/>
            ) : activationDetailsError ? (
                <p role="alert" className="text-[13px] text-[#B3261E]">{t('activation.touchpoints.error')}</p>
            ) : activationDetails.length === 0 ? (
                <p className="text-[13px] text-neutralBlack-600">{t('activation.touchpoints.noActivation')}</p>
            ) : activationDetails.map((activation) => (
                <ActivationTouchpointsCard key={activation.id} activation={activation}/>
            ))}
        </section>
    )
}
